import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import { useCart } from '../context/CartContext';

export interface ProductCardProduct {
  id: string | number;
  name: string;
  img?: string;
  tag?: string;
  cat?: string;
}

interface ProductCardProps {
  product: ProductCardProduct;
  /** Show "best seller" badge (e.g. first item in featured list) */
  showBestSeller?: boolean;
  /** Optional wrapper className for grid vs carousel (e.g. min-w for carousel) */
  className?: string;
  /** Optional layout style */
  layout?: 'grid' | 'list';
}

const ProductCard: React.FC<ProductCardProps> = ({ product, showBestSeller = false, className = '', layout = 'grid' }) => {
  const isList = layout === 'list';
  const [imgError, setImgError] = useState(false);
  const [added, setAdded] = useState(false);
  const imgSrc = product.img?.trim() ?? '';
  const showImg = imgSrc && !imgError;
  const { addItem } = useCart();

  const detailUrl = `/shop/${product.id}`;

  const handleSendToEnquiry = (e: React.MouseEvent) => {
    e.preventDefault();
    addItem({
      id: product.id,
      name: product.name,
      cat: product.cat ?? '',
      tag: product.tag,
      img: product.img ?? '',
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  return (
    <div className={`flex ${isList ? 'flex-col sm:flex-row gap-6 items-center w-full border border-gray-100 rounded-2xl p-4 bg-white hover:shadow-lg transition-shadow' : 'flex-col flex-shrink-0 h-full min-h-0 pb-4 md:pb-0'} ${className}`}>
      <Link to={detailUrl} className={`${isList ? 'w-full sm:w-1/3 lg:w-48 flex-shrink-0 mb-0 aspect-square rounded-2xl sm:rounded-[2rem] p-6 sm:p-8' : 'mb-3 sm:mb-5 flex-shrink-0 w-full h-44 sm:h-48 md:h-52 rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8'} bg-[#F7F9FC] relative flex items-center justify-center group overflow-hidden border border-gray-100`}>
        <div className="absolute top-5 left-5 bg-white border border-gray-200 px-3 py-1 rounded-full text-[10px] font-bold text-gray-500 uppercase tracking-widest shadow-sm z-10">
          In Stock
        </div>
        {showBestSeller && !isList && (
          <div className="absolute top-4 right-4 bg-[#BDE0FE] text-primary w-14 h-14 rounded-full flex items-center justify-center text-center text-[10px] font-bold leading-tight shadow-sm z-10 rotate-12">
            best<br/>seller
          </div>
        )}
        {showImg ? (
          <img
            src={imgSrc}
            alt={product.name}
            className="w-full h-full object-contain mix-blend-multiply group-hover:scale-110 transition-transform duration-700 ease-out"
            onError={() => setImgError(true)}
            referrerPolicy="no-referrer"
          />
        ) : (
          <MedicalServicesIcon sx={{ fontSize: 64 }} className="text-gray-300" />
        )}
      </Link>
      
      <div className={`px-2 flex-1 flex flex-col min-h-0 flex-grow ${isList ? 'pt-2' : ''}`}>
        <div className="flex justify-between items-start mb-1 gap-4 min-h-0">
          <Link to={detailUrl} className={`font-extrabold text-primary leading-tight flex-1 min-w-0 hover:text-accent transition-colors line-clamp-2 ${isList ? 'text-xl line-clamp-none' : 'text-base'}`}>
            {product.name.toLowerCase()}
          </Link>
          <div className="flex text-accent text-[10px] mt-1 space-x-[2px] opacity-80 flex-shrink-0">
            <span>★</span><span>★</span><span>★</span><span>★</span><span>★</span>
          </div>
        </div>
        <p className={`text-gray-500 truncate flex-shrink-0 ${isList ? 'text-base mb-4' : 'text-sm mb-3'}`}>{product.tag || product.cat}</p>
        
        {isList && (
           <p className="text-sm text-gray-600 mb-6 max-w-2xl leading-relaxed">
             This high-quality {product.tag ? product.tag.toLowerCase() : 'medical'} equipment is engineered for professional hospital and laboratory environments. Ensures durability, precision, and reliable performance across varying medical procedures.
           </p>
        )}

        {/* On phone: left-align "view details" / "send to enquiry" at bottom. On md+: keep description left, links right. */}
        <div className={`border-t border-gray-100 pt-3 mt-auto flex-shrink-0 min-h-[80px] md:min-h-[72px] flex flex-col items-start gap-2 md:flex-row md:justify-between md:items-end`}>
          <p className="text-xs text-gray-500 leading-relaxed order-first md:pr-4">Engineered for medical professionals.</p>
          <div className="flex flex-col items-start gap-1 flex-shrink-0 md:items-end">
            <Link to={detailUrl} className={`font-bold transition-colors underline underline-offset-4 whitespace-nowrap ${isList ? 'text-sm btn-primary !py-2 !px-5 no-underline hover:text-white' : 'text-xs text-primary hover:text-accent'}`}>
              {isList ? 'Request Quote' : 'view details'}
            </Link>
            <button
              type="button"
              onClick={handleSendToEnquiry}
              className={`font-bold transition-colors underline underline-offset-4 whitespace-nowrap text-xs text-primary hover:text-accent ${isList ? 'text-sm' : ''} ${added ? 'text-green-600' : ''}`}
            >
              {added ? 'Added ✓' : 'send to enquiry'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
